// StudentCaseStudies.jsx - mirrors StudentResources.jsx (same course-filter pattern)
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../usercomponents/SideBar';
import TopNavBar from '../usercomponents/TopNavBar';
import Footer from '../usercomponents/Footer';
const apiUrl = process.env.REACT_APP_API_URL;

function StudentCaseStudies() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [courses, setCourses] = useState([]); // filter options: [{ id, coursename }]
  const [selectedCourse, setSelectedCourse] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const itemsPerPage = 10;
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setError('');
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('studentSession'));
      if (!sessionData || !sessionData.token) {
        setError('You must be logged in.');
        setLoading(false);
        return;
      }
      const authHeader = { headers: { Authorization: `Bearer ${sessionData.token}` } };

      const [caseStudiesRes, coursesRes] = await Promise.all([
        axios.get(`${apiUrl}/api/student/case-studies`, authHeader),
        axios.get(`${apiUrl}/api/student/courses`, authHeader),
      ]);

      const fetched = caseStudiesRes.data.success ? caseStudiesRes.data.caseStudies : [];
      setCaseStudies(fetched);

      // Filter options: courses the student joined, unioned with any course a
      // case study actually belongs to (so every listed PDF stays filterable).
      const courseMap = new Map();
      if (coursesRes.data.success) {
        (coursesRes.data.courses || []).forEach((c) => {
          courseMap.set(String(c.id), c.coursename);
        });
      }
      fetched.forEach((cs) => {
        if (cs.course_id != null && !courseMap.has(String(cs.course_id))) {
          courseMap.set(String(cs.course_id), cs.coursename);
        }
      });
      const courseList = Array.from(courseMap, ([id, coursename]) => ({ id, coursename }));
      setCourses(courseList);

      // One course -> auto-select it. More than one -> default to ALL.
      setSelectedCourse(courseList.length === 1 ? courseList[0].id : 'ALL');
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading case studies.');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (caseStudy) => {
    let filename = '';
    if (caseStudy.pdf) {
      filename = caseStudy.pdf.split('/').pop();
    }
    if (!filename) {
      alert('Error: Could not extract filename from PDF path');
      return;
    }
    const pdfUrl = `${apiUrl}/api/public/case-study-pdf?file=${encodeURIComponent(filename)}`;
    navigate('/student-view-case-study', {
      state: {
        bookTitle: caseStudy.title,
        pdfUrl: pdfUrl,
        courseName: caseStudy.coursename,
        filename: filename
      }
    });
  };

  const handleFilterChange = (value) => {
    setSelectedCourse(value);
    setCurrentPage(1);
  };

  const filteredCaseStudies = selectedCourse === 'ALL'
    ? caseStudies
    : caseStudies.filter((cs) => String(cs.course_id) === String(selectedCourse));

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentCaseStudies = filteredCaseStudies.slice(indexOfFirst, indexOfLast);
  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(filteredCaseStudies.length / itemsPerPage); i++) {
    pageNumbers.push(i);
  }

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb mb-4">
            <ul className="d-flex align-items-center gap-2 list-unstyled">
              <li><Link to="/dashboard" className="text-gray-200 text-15 hover-text-main-600 text-decoration-none">Home</Link></li>
              <li><span className="text-gray-500 d-flex align-items-center"><i className="ph ph-caret-right" /></span></li>
              <li><span className="text-main-600 text-15">Case Studies</span></li>
            </ul>
          </div>
          <div className="card overflow-hidden">
            <div className="card-body p-24">
              <h4 className="mb-16">Case Studies</h4>
              {error && <div className="alert alert-danger mb-16">{error}</div>}
              {!loading && !error && courses.length > 0 && (
                <div className="d-flex align-items-center gap-2 flex-wrap" style={{ maxWidth: 400 }}>
                  <label htmlFor="caseStudyCourseFilter" className="h6 mb-0 text-gray-300 flex-shrink-0">
                    Filter by course:
                  </label>
                  <select
                    id="caseStudyCourseFilter"
                    className="form-control py-11"
                    value={selectedCourse}
                    onChange={(e) => handleFilterChange(e.target.value)}
                  >
                    {courses.length > 1 && <option value="ALL">All Courses</option>}
                    {courses.map((c) => (
                      <option key={c.id} value={c.id}>{c.coursename}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="card-body p-0 overflow-x-auto">
              {loading && <div className="alert alert-info mb-0" role="alert">Loading case studies...</div>}
              {error && <div className="alert alert-danger mb-0" role="alert">{error}</div>}
              {!loading && !error && caseStudies.length === 0 && (
                <div className="alert alert-warning mb-0" role="alert">
                  No case studies available.
                </div>
              )}
              {!loading && !error && caseStudies.length > 0 && filteredCaseStudies.length === 0 && (
                <div className="alert alert-warning mb-0" role="alert">
                  No case studies for the selected course.
                </div>
              )}
              {!loading && !error && filteredCaseStudies.length > 0 && (
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th className="h6 text-gray-300">Title</th>
                      <th className="h6 text-gray-300">Course</th>
                      <th className="h6 text-gray-300">Created</th>
                      <th className="h6 text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentCaseStudies.map((cs) => (
                      <tr key={cs.id}>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {cs.title.length > 30 ? `${cs.title.substring(0, 30)}...` : cs.title}
                          </span>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {cs.coursename.length > 30 ? `${cs.coursename.substring(0, 30)}...` : cs.coursename}
                          </span>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {new Date(cs.created).toLocaleDateString()}
                          </span>
                        </td>
                        <td>
                          <button
                            onClick={() => handleView(cs)}
                            className="bg-main-50 text-main-600 py-2 px-14 rounded-pill hover-bg-main-600 hover-text-white"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="card-footer flex-between flex-wrap">
              <span className="text-gray-900">
                Showing {filteredCaseStudies.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredCaseStudies.length)} of {filteredCaseStudies.length} entries
              </span>
              <ul className="pagination flex-align flex-wrap">
                {pageNumbers.map(number => (
                  <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                    <a
                      onClick={() => setCurrentPage(number)}
                      className="page-link h-44 w-44 flex-center text-15 rounded-8 fw-medium"
                      href="#!"
                    >
                      {number}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default StudentCaseStudies;
